// src/components/Works/Works.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link as ScrollLink } from "react-scroll";
import {
  FiArrowUpRight,
  FiCheck,
  FiCode,
  FiPlay,
  FiX,
} from "react-icons/fi";
import ThemedButton from "../ThemeButton/ThemedButton";
import Portfolio1 from "../../assets/spiderlegs.webp";
import Portfolio2 from "../../assets/instagram_versio.png";
import LogoOMG from "../../assets/Facelogo.jpg";
import LogoNapatoisto from "../../assets/Napatoiston logo.webp";
import LogoProGreen from "../../assets/Pro_green_logo.webp";
import LogoBuukkeri from "../../assets/buukkeri-logo.webp";
import LogoParanoidi from "../../assets/paranoidi-favicon-512.png";
import LogoEcocreative from "../../assets/ecocreative.webp";
import MockupBuukkeri from "../../assets/mock-up-buukkeri.webp";
import MockupJoyride from "../../assets/mock-up-joyride.webp";
import MockupMatti from "../../assets/mock-up-matti.webp";
import MockupPekka from "../../assets/mock-up-pekka.webp";
import MockupNicoBertula from "../../assets/mock-up-nico-bertula2.png";
import MockupSpiderlegs from "../../assets/mock-up-spiderlegs.webp";
import MockupParanoidi from "../../assets/mock-up-paranoidi.png";
import MockupRelicpaja from "../../assets/mock-up-relicpaja-new.png";
import {
  t as translate,
  normalizeLang,
  getLangFromPathname,
} from "../../i18n";
import { deepMerge, useSiteContent } from "../../content/SiteContentContext";
const uiCopy = {
  fi: {
    badge: "Referenssit",
    title: "Toteutuksia, jotka ovat oikeasti käytössä",
    lead:
      "Kaikki alla olevat projektit ovat julkaistuja toteutuksia. Mukana on täysin räätälöityjä verkkosivustoja, artistisivustoja sekä full-stack-verkkopalveluita.",
    proof: [
      "8 julkaistua toteutusta",
      "WordPress + React + Tailwind",
      "Node.js + tietokannat",
      "SEO + GA4",
    ],
    featuredEyebrow: "Laajemmat toteutukset",
    featuredTitle: "Verkkopalvelut ja työkalut",
    featuredLead:
      "Näissä projekteissa käyttöliittymä, taustajärjestelmä, tietokanta ja liiketoimintalogiikka muodostavat yhden toimivan kokonaisuuden.",
    sitesEyebrow: "Verkkosivut",
    sitesTitle: "Julkaistut verkkosivut",
    sitesLead:
      "Tutustu erilaisiin verkkosivuihin, artistisivustoihin ja palveluihin, jotka on suunniteltu omien tavoitteidensa, yleisönsä ja brändinsä ympärille.",
    openSite: "Katso sivusto",
    techLabel: "Tekniikat",
    logosEyebrow: "Visuaalinen suunnittelu",
    logosTitle: "Logot ja brändit",
    logosLead:
      "Valikoima logoja ja visuaalisia ilmeitä bändeille, yrityksille, palveluille ja erilaisiin projekteihin.",
    videosEyebrow: "Videoeditointi",
    videosTitle: "Videoeditointi ja mediasisällöt",
    videosLead:
      "Promovideoita, musiikkivideoita ja muuta videosisältöä artisteille, bändeille ja erilaisiin projekteihin.",
    playVideo: "Katso video",
    closeVideo: "Sulje video",
    pricingEyebrow: "Seuraava askel",
    pricingTitle: "Mitä vastaava toteutus maksaa?",
    pricingLead:
      "Katso suuntaa-antavat hintatasot tai kerro projektistasi suoraan. Toteutus ja hinta määräytyvät aina tarpeen mukaan.",
    pricingCta: "Katso verkkosivujen hinnat",
    contactCta: "Kerro projektistasi",
    businessCta: "Verkkosivut yritykselle",
  },
  en: {
    badge: "Selected work",
    title: "Projects that are live and in real use",
    lead:
      "Every project below is a published implementation. The portfolio includes fully custom websites, artist websites and full-stack web services.",
    proof: [
      "8 live projects",
      "WordPress + React + Tailwind",
      "Node.js + databases",
      "SEO + GA4",
    ],
    featuredEyebrow: "Larger builds",
    featuredTitle: "Web platforms and tools",
    featuredLead:
      "In these projects the interface, backend, database and business logic work together as one production-ready service.",
    sitesEyebrow: "Websites",
    sitesTitle: "Published websites",
    sitesLead:
      "Explore websites, artist sites and digital services designed around different goals, audiences and brand identities.",
    openSite: "View website",
    techLabel: "Technology",
    logosEyebrow: "Visual design",
    logosTitle: "Logos and brands",
    logosLead:
      "A selection of logos and visual identities created for bands, businesses, services and other projects.",
    videosEyebrow: "Video editing",
    videosTitle: "Video editing and media content",
    videosLead:
      "Promotional videos, music videos and other visual content for artists, bands and different projects.",
    playVideo: "Watch video",
    closeVideo: "Close video",
    pricingEyebrow: "Next step",
    pricingTitle: "What would a similar project cost?",
    pricingLead:
      "See indicative price levels or tell me about your project directly. Scope and pricing are always based on what the project actually needs.",
    pricingCta: "See website pricing",
    contactCta: "Tell me about your project",
    businessCta: "Business websites",
  },
  es: {
    badge: "Referencias",
    title: "Proyectos publicados y en uso real",
    lead:
      "Todos los proyectos de abajo están publicados. El portfolio incluye webs totalmente a medida, webs para artistas y servicios web full-stack.",
    proof: [
      "8 proyectos publicados",
      "WordPress + React + Tailwind",
      "Node.js + bases de datos",
      "SEO + GA4",
    ],
    featuredEyebrow: "Proyectos más amplios",
    featuredTitle: "Plataformas web y herramientas",
    featuredLead:
      "En estos proyectos la interfaz, el backend, la base de datos y la lógica de negocio forman un único servicio preparado para producción.",
    sitesEyebrow: "Sitios web",
    sitesTitle: "Webs publicadas",
    sitesLead:
      "Descubre webs, sitios para artistas y servicios digitales diseñados alrededor de distintos objetivos, públicos e identidades de marca.",
    openSite: "Ver sitio web",
    techLabel: "Tecnología",
    logosEyebrow: "Diseño visual",
    logosTitle: "Logos y marcas",
    logosLead:
      "Una selección de logotipos e identidades visuales creadas para bandas, empresas, servicios y otros proyectos.",
    videosEyebrow: "Edición de vídeo",
    videosTitle: "Edición de vídeo y contenido audiovisual",
    videosLead:
      "Vídeos promocionales, vídeos musicales y otros contenidos audiovisuales para artistas, bandas y distintos proyectos.",
    playVideo: "Ver vídeo",
    closeVideo: "Cerrar vídeo",
    pricingEyebrow: "Siguiente paso",
    pricingTitle: "¿Cuánto costaría un proyecto parecido?",
    pricingLead:
      "Consulta precios orientativos o cuéntame directamente tu proyecto. El alcance y el precio dependen siempre de las necesidades reales.",
    pricingCta: "Ver precios de páginas web",
    contactCta: "Cuéntame tu proyecto",
    businessCta: "Webs para empresas",
  },
};
const cases = [
  {
    id: "buukkeri",
    featured: true,
    href: "https://buukkeri.fi/",
    img: MockupBuukkeri,
    alt: "Buukkeri",
    tech: ["WordPress", "React", "Tailwind CSS", "Node.js / Express", "PostgreSQL"],
    localized: {
      fi: {
        eyebrow: "Varauspalvelu / full-stack",
        title: "Buukkeri.fi",
        desc:
          "Esiintyjien ja tapahtumapalveluiden varauspalvelu, jossa asiakas voi vertailla palveluntarjoajia, hintoja ja saatavuutta sekä edetä tarjouspyyntöön yhdestä paikasta. Julkinen hakupalvelu, esiintyjäprofiilit, kalenterit, varauslogiikka, SEO-sivut ja mittaus toimivat saman järjestelmän sisällä.",
        bullets: [
          "Profiilit, hinnat, saatavuuskalenterit ja tarjouspyyntöpolku",
          "Node.js / Express -backend ja PostgreSQL-tietokanta",
          "SEO-länderit, GA4-mittaus ja konversioiden seuranta",
        ],
      },
      en: {
        eyebrow: "Booking platform / full-stack",
        title: "Buukkeri.fi",
        desc:
          "A booking platform for performers and event services where customers can compare providers, pricing and availability and continue to an enquiry in one place. Public search, performer profiles, calendars, booking logic, SEO landing pages and analytics are integrated into the same system.",
        bullets: [
          "Profiles, pricing, availability calendars and enquiry flow",
          "Node.js / Express backend with PostgreSQL",
          "SEO landing pages, GA4 analytics and conversion tracking",
        ],
      },
      es: {
        eyebrow: "Plataforma de reservas / full-stack",
        title: "Buukkeri.fi",
        desc:
          "Una plataforma de reservas para artistas y servicios de eventos donde el cliente puede comparar proveedores, precios y disponibilidad y continuar hasta la solicitud de oferta desde un único lugar. La búsqueda pública, los perfiles, calendarios, lógica de reserva, páginas SEO y analítica forman parte del mismo sistema.",
        bullets: [
          "Perfiles, precios, calendarios de disponibilidad y flujo de solicitud",
          "Backend con Node.js / Express y PostgreSQL",
          "Landings SEO, analítica GA4 y seguimiento de conversiones",
        ],
      },
    },
  },
  {
    id: "paranoidi",
    featured: true,
    href: "https://paranoidi.fi/",
    img: MockupParanoidi,
    alt: "Paranoidi",
    tech: ["WordPress", "React", "Tailwind CSS", "Node.js / Express", "PostgreSQL"],
    localized: {
      fi: {
        eyebrow: "SaaS / keikkatyökalu",
        title: "Paranoidi",
        desc:
          "Esiintyjille rakennettu digitaalinen keikkatyökalu, jossa yleisö lähettää biisitoiveita QR-linkin kautta ja esiintyjä hallitsee toiveita, biisikirjastoa ja keikan aikaista työnkulkua omasta näkymästään. Palvelu on suunniteltu käytettäväksi oikeasti kesken keikan.",
        bullets: [
          "Julkinen biisitoivesivu, QR-linkit ja esiintyjän hallintanäkymä",
          "Node.js / Express -backend ja PostgreSQL-tietokanta",
          "Biisikirjasto, toivehallinta, tilausmallit ja keikkakäytön työkalut",
        ],
      },
      en: {
        eyebrow: "SaaS / live gig tool",
        title: "Paranoidi",
        desc:
          "A digital live-gig tool built for performers. The audience can send song requests through a QR link while the performer manages requests, the song library and the live workflow from a dedicated interface. The service is designed for real use during a performance.",
        bullets: [
          "Public song-request page, QR links and performer dashboard",
          "Node.js / Express backend with PostgreSQL",
          "Song library, request management, subscription models and live-use tools",
        ],
      },
      es: {
        eyebrow: "SaaS / herramienta para directos",
        title: "Paranoidi",
        desc:
          "Una herramienta digital para artistas en directo. El público puede enviar peticiones de canciones mediante un enlace QR y el artista gestiona las peticiones, la biblioteca musical y el flujo del concierto desde su propio panel. El servicio está pensado para utilizarse durante una actuación real.",
        bullets: [
          "Página pública de peticiones, enlaces QR y panel del artista",
          "Backend con Node.js / Express y PostgreSQL",
          "Biblioteca musical, gestión de peticiones, suscripciones y herramientas para directo",
        ],
      },
    },
  },
  {
    id: "spiderlegs",
    href: "https://spiderlegsproductions.com/",
    img: MockupSpiderlegs,
    alt: "Spiderlegs Productions",
    tech: ["WordPress", "React", "Tailwind CSS", "Node.js / Express", "PostgreSQL"],
    localized: {
      fi: {
        eyebrow: "Vuokraussivusto / full-stack",
        title: "Spiderlegs Productions",
        desc:
          "Äänentoiston, esitystekniikan ja laitevuokrauksen sivusto, jossa palvelut, vuokrattava kalusto ja yhteydenottopolku on rakennettu asiakkaalle mahdollisimman selkeäksi. Sivusto toimii samalla yrityksen myyntikanavana ja vuokraustoiminnan digitaalisena käyttöliittymänä.",
        bullets: [
          "WordPress + React + Tailwind -käyttöliittymä",
          "Node.js / Express -backend ja PostgreSQL-tietokanta",
          "Palvelut, laitevuokraus, varauspolku ja GA4-mittaus",
        ],
      },
      en: {
        eyebrow: "Rental website / full-stack",
        title: "Spiderlegs Productions",
        desc:
          "A website for sound systems, event technology and equipment rental where services, rental inventory and the enquiry path are structured to be easy for the customer to understand. The site also works as the company's sales channel and digital interface for the rental business.",
        bullets: [
          "WordPress + React + Tailwind interface",
          "Node.js / Express backend with PostgreSQL",
          "Services, equipment rental, booking flow and GA4 analytics",
        ],
      },
      es: {
        eyebrow: "Web de alquiler / full-stack",
        title: "Spiderlegs Productions",
        desc:
          "Una web para sonido, tecnología de eventos y alquiler de equipos donde los servicios, el material disponible y el proceso de contacto están organizados para que el cliente avance con claridad. La web funciona también como canal de ventas e interfaz digital del negocio de alquiler.",
        bullets: [
          "Interfaz con WordPress + React + Tailwind",
          "Backend con Node.js / Express y PostgreSQL",
          "Servicios, alquiler de equipos, flujo de reserva y analítica GA4",
        ],
      },
    },
  },
  {
    id: "joyride",
    href: "https://bilebandijoyride.com/",
    img: MockupJoyride,
    alt: "Bilebändi Joyride",
    tech: ["WordPress", "React", "Tailwind CSS"],
    localized: {
      fi: {
        eyebrow: "Bändisivusto",
        title: "Bilebändi Joyride",
        desc:
          "Bilebändille rakennettu oma myyntisivusto, jossa visuaalinen ilme, musiikki, videot, sisältö ja yhteydenotto tukevat keikkamyyntiä. Rakenne on suunniteltu toimimaan sekä suoraan sivulle tuleville asiakkaille että Google- ja someliikenteelle.",
        bullets: [
          "WordPress + React + Tailwind -toteutus",
          "Bändin brändi, videot, sisältö ja selkeä yhteydenottopolku",
          "Hakukoneille luettava rakenne ja mobiiliin optimoitu käyttöliittymä",
        ],
      },
      en: {
        eyebrow: "Band website",
        title: "Bilebändi Joyride",
        desc:
          "A dedicated sales website for a live party band where visual identity, music, video, content and contact options are built around gig bookings. The structure supports visitors arriving directly as well as traffic from Google and social media.",
        bullets: [
          "WordPress + React + Tailwind implementation",
          "Band identity, video, content and a clear booking/contact path",
          "Search-friendly structure and a mobile-optimised interface",
        ],
      },
      es: {
        eyebrow: "Web para banda",
        title: "Bilebändi Joyride",
        desc:
          "Una web comercial propia para una banda de fiestas donde la identidad visual, la música, los vídeos, el contenido y el contacto están orientados a conseguir actuaciones. La estructura funciona tanto para visitas directas como para tráfico desde Google y redes sociales.",
        bullets: [
          "Implementación con WordPress + React + Tailwind",
          "Identidad de banda, vídeos, contenido y ruta clara de contacto",
          "Estructura preparada para buscadores e interfaz optimizada para móvil",
        ],
      },
    },
  },
  {
    id: "trubaduuripekka",
    href: "https://www.trubaduuripekka.fi/",
    img: MockupPekka,
    alt: "Trubaduuri Pekka",
    tech: ["WordPress", "React", "Tailwind CSS"],
    localized: {
      fi: {
        eyebrow: "Trubaduurisivusto",
        title: "Trubaduuri Pekka",
        desc:
          "Trubaduurille ja eri kokoonpanoille rakennettu moderni sivusto, jossa musiikki, videot, kuvat, palvelut ja WhatsApp-yhteydenotto tukevat keikkamyyntiä. Uudistuksessa tärkeää oli säilyttää jo saavutettu hakukonenäkyvyys samalla kun koko käyttöliittymä modernisoitiin.",
        bullets: [
          "WordPress + React + Tailwind -toteutus",
          "Trubaduuri-, duo-, trio- ja bändikokoonpanot samalla sivustolla",
          "Videot, kuvat, WhatsApp-yhteydenotto ja SEO-rakenteen säilyttäminen",
        ],
      },
      en: {
        eyebrow: "Troubadour website",
        title: "Trubaduuri Pekka",
        desc:
          "A modern website built for troubadour Trubaduuri Pekka and his different line-ups, combining music, videos, photography, services and WhatsApp contact to support gig bookings. A key part of the rebuild was preserving existing search visibility while modernising the entire interface.",
        bullets: [
          "WordPress + React + Tailwind implementation",
          "Troubadour, duo, trio and band line-ups on one website",
          "Video, photography, WhatsApp contact and preserved SEO structure",
        ],
      },
      es: {
        eyebrow: "Web para trovador",
        title: "Trubaduuri Pekka",
        desc:
          "Una web moderna creada para el trovador Trubaduuri Pekka y sus distintas formaciones, reuniendo música, vídeos, fotografía, servicios y contacto por WhatsApp para apoyar la contratación. Una parte clave de la renovación fue conservar la visibilidad ya conseguida en buscadores mientras se modernizaba toda la interfaz.",
        bullets: [
          "Implementación con WordPress + React + Tailwind",
          "Formaciones de trovador, dúo, trío y banda en una sola web",
          "Vídeos, fotografía, contacto por WhatsApp y estructura SEO preservada",
        ],
      },
    },
  },
  {
    id: "mattioksa",
    href: "https://www.mattioksa.fi/",
    img: MockupMatti,
    alt: "Trubaduuri Matti Oksa",
    tech: ["WordPress", "React", "Tailwind CSS", "Node.js", "SQLite (Paranoidi)"],
    localized: {
      fi: {
        eyebrow: "Trubaduurisivusto + työkalut",
        title: "Trubaduuri Matti Oksa",
        desc:
          "Trubaduurille rakennettu laajempi artistisivusto, jossa keikkamyynti, ohjelmisto, keikkakalenteri ja TikTok-liveaikojen hallinta yhdistyvät Paranoidi-biisitoivetyökaluun. Kokonaisuus palvelee sekä yleisöä että artistin omaa keikka-arkea.",
        bullets: [
          "WordPress + React + Tailwind -toteutus",
          "Keikkakalenteri, ohjelmistohaku ja TikTok-liveaikojen hallinta",
          "Paranoidi-integraatio: Node.js + SQLite biisitoiveiden hallintaan",
        ],
      },
      en: {
        eyebrow: "Troubadour website + tools",
        title: "Trubaduuri Matti Oksa",
        desc:
          "A broader artist website built for troubadour Matti Oksa, combining gig sales, repertoire, a gig calendar and TikTok Live schedule management with the Paranoidi song-request tool. The result supports both the audience experience and the artist's day-to-day live work.",
        bullets: [
          "WordPress + React + Tailwind implementation",
          "Gig calendar, repertoire search and TikTok Live schedule management",
          "Paranoidi integration: Node.js + SQLite for song-request management",
        ],
      },
      es: {
        eyebrow: "Web para trovador + herramientas",
        title: "Trubaduuri Matti Oksa",
        desc:
          "Una web de artista más amplia creada para el trovador Matti Oksa, que combina contratación, repertorio, calendario de actuaciones y gestión de horarios de TikTok Live con la herramienta Paranoidi para peticiones de canciones. El conjunto sirve tanto al público como al trabajo diario del artista.",
        bullets: [
          "Implementación con WordPress + React + Tailwind",
          "Calendario, búsqueda de repertorio y gestión de horarios de TikTok Live",
          "Integración Paranoidi: Node.js + SQLite para gestionar peticiones",
        ],
      },
    },
  },
  {
    id: "nicobertula",
    href: "https://nicobertula.fi/",
    img: MockupNicoBertula,
    alt: "Nico Bertula",
    tech: ["WordPress", "React", "Tailwind CSS"],
    localized: {
      fi: {
        eyebrow: "Artistisivusto",
        title: "Nico Bertula",
        desc:
          "Artistisivusto, joka kokoaa Nico Bertulan eri kokoonpanot, ohjelmiston, livevideot, keikkakalenterin ja yhteydenoton yhdeksi helposti hahmotettavaksi kokonaisuudeksi. Sivusto palvelee sekä yksityis- että yritysasiakkaita ilman että sisältö hajoaa useisiin erillisiin palveluihin.",
        bullets: [
          "WordPress + React + Tailwind -toteutus",
          "Trubaduuri, duo, Trio Ohoi ja B&T Live samalla sivustolla",
          "Ohjelmisto, livevideot, keikkakalenteri, yhteydenotto ja SEO-perusta",
        ],
      },
      en: {
        eyebrow: "Artist website",
        title: "Nico Bertula",
        desc:
          "An artist website that brings Nico Bertula's different line-ups, repertoire, live videos, gig calendar and contact options into one clear experience. The site serves both private and corporate customers without splitting the content across separate services.",
        bullets: [
          "WordPress + React + Tailwind implementation",
          "Troubadour, duo, Trio Ohoi and B&T Live on one website",
          "Repertoire, live videos, gig calendar, contact path and SEO foundation",
        ],
      },
      es: {
        eyebrow: "Web de artista",
        title: "Nico Bertula",
        desc:
          "Una web de artista que reúne las distintas formaciones de Nico Bertula, repertorio, vídeos en directo, calendario de actuaciones y contacto en una experiencia clara. La web sirve tanto a clientes particulares como a empresas sin dividir el contenido entre servicios separados.",
        bullets: [
          "Implementación con WordPress + React + Tailwind",
          "Trovador, dúo, Trio Ohoi y B&T Live en una sola web",
          "Repertorio, vídeos en directo, calendario, contacto y base SEO",
        ],
      },
    },
  },
  {
    id: "relicpaja",
    href: "https://relicpaja.fi/",
    img: MockupRelicpaja,
    alt: "Relicpaja",
    tech: ["WordPress", "React", "Tailwind CSS"],
    localized: {
      fi: {
        eyebrow: "Palvelusivusto",
        title: "Relicpaja – relic-kitarapalvelu",
        desc:
          "Relic-kitarapalvelulle rakennettu vahvasti visuaalinen sivusto, jossa palvelut, tuotteet, työnäytteet ja yhteydenotto esitellään yhtenä selkeänä kokonaisuutena. FI/EN-rakenne ja hallittava kuvagalleria tekevät sivusta sekä myyvän että helposti ylläpidettävän.",
        bullets: [
          "WordPress + React + Tailwind -toteutus",
          "FI/EN-kieliversiot, palvelut, tuotteet ja hallittava galleria",
          "Hakukoneille luettava rakenne ja selkeä yhteydenottopolku",
        ],
      },
      en: {
        eyebrow: "Service website",
        title: "Relicpaja – guitar relicing service",
        desc:
          "A strongly visual website built for a guitar relicing service, bringing services, products, finished work and contact information into one clear experience. The FI/EN structure and manageable gallery make the site both sales-focused and easy to maintain.",
        bullets: [
          "WordPress + React + Tailwind implementation",
          "FI/EN language versions, services, products and manageable gallery",
          "Search-readable structure and a clear contact path",
        ],
      },
      es: {
        eyebrow: "Web de servicios",
        title: "Relicpaja – servicio de relicado de guitarras",
        desc:
          "Una web muy visual creada para un servicio de relicado de guitarras, donde servicios, productos, trabajos realizados y contacto forman una experiencia clara. La estructura FI/EN y la galería administrable hacen que la web sea comercial y fácil de mantener.",
        bullets: [
          "Implementación con WordPress + React + Tailwind",
          "Versiones FI/EN, servicios, productos y galería administrable",
          "Estructura legible para buscadores y ruta clara de contacto",
        ],
      },
    },
  },
];
const logos = [
  { name: "0MG", src: LogoOMG, link: "https://www.facebook.com/Bilebandi0MG" },
  { name: "Bilebändi Joyride", src: Portfolio2, link: "https://bilebandijoyride.com/" },
  { name: "Napatoisto", src: LogoNapatoisto, link: "https://soundcloud.com/napatoisto-yhtye" },
  {
    name: "Pro Green",
    src: LogoProGreen,
    link: "https://www.taitotalo.fi/ajankohtaista/hankkeet-ja-projektit-taitotalossa/kansainvalinen-toiminta-erasmushankkeet/erasmus-ka-210-vet-pro-green-way-arts-and-crafts",
  },
  { name: "Spiderlegs Productions", src: Portfolio1, link: "https://spiderlegsproductions.com/" },
  { name: "Paranoidi", src: LogoParanoidi, link: "https://paranoidi.fi/" },
  { name: "Buukkeri", src: LogoBuukkeri, link: "https://buukkeri.fi/" },
  { name: "Ecocreative", src: LogoEcocreative, link: "" },
];
const videos = [
  {
    id: "m7fNg_ViaFQ",
    title: {
      fi: "Bilebändi Joyride – Nopeat promovideo",
      en: "Bilebändi Joyride – Fast songs promo video",
      es: "Bilebändi Joyride – Vídeo promocional de canciones rápidas",
    },
  },
  {
    // Huom: lähdetiedostossa myös hitaiden biisien videolla on sama YouTube-ID.
    // Vaihda tähän oikea ID, kun se on tiedossa.
    id: "m7fNg_ViaFQ",
    title: {
      fi: "Bilebändi Joyride – Hitaiden biisien promovideo 2025",
      en: "Bilebändi Joyride – Slow songs promo video 2025",
      es: "Bilebändi Joyride – Vídeo promocional de canciones lentas 2025",
    },
  },
  {
    id: "xYuvqijBV9M",
    title: {
      fi: "Korjosen Anneli – Yrjöö nurkissa",
      en: "Korjosen Anneli – Yrjöö nurkissa",
      es: "Korjosen Anneli – Yrjöö nurkissa",
    },
  },
  {
    id: "6PxIiYBV9xU",
    title: {
      fi: "Saara Aalto – Odota (Aikakone drumcover)",
      en: "Saara Aalto – Odota (Aikakone drum cover)",
      es: "Saara Aalto – Odota (drum cover de Aikakone)",
    },
  },
];
const ProjectImage = ({ project, featured = false, openLabel = "Avaa" }) => {
  const containImage = ["paranoidi", "relicpaja", "nicobertula"].includes(
    project.id
  );
  return (
    <a
      href={project.href}
      target="_blank"
      rel="noreferrer"
      className="group/image block overflow-hidden rounded-[1.5rem] border border-black/10 bg-white/70 p-2 shadow-[0_24px_70px_rgba(0,0,0,0.14)] transition hover:-translate-y-1 hover:shadow-[0_30px_90px_rgba(0,0,0,0.2)] focus:outline-none focus:ring-2 focus:ring-lime-400/70 dark:border-white/10 dark:bg-black/20 dark:shadow-[0_28px_80px_rgba(0,0,0,0.45)] sm:p-3"
      aria-label={`${openLabel}: ${project.alt}`}
    >
      <div className="relative overflow-hidden rounded-[1.15rem] bg-black/[0.03] dark:bg-black/20">
        <img
          src={project.img}
          alt={project.alt}
          loading="lazy"
          className={`${
            featured ? "aspect-[16/9]" : "aspect-[16/10]"
          } w-full transition duration-500 group-hover/image:scale-[1.015] ${
            containImage ? "object-contain" : "object-cover"
          }`}
        />
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(120deg,transparent_25%,rgba(255,255,255,0.22)_45%,transparent_62%)] opacity-0 transition-opacity duration-500 group-hover/image:opacity-100 dark:bg-[linear-gradient(120deg,transparent_25%,rgba(255,255,255,0.1)_45%,transparent_62%)]" />
      </div>
    </a>
  );
};
const Works = ({ lang: langProp } = {}) => {
  const [isReady, setIsReady] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
  const siteContent = useSiteContent();
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "/";
  const lang =
    typeof langProp === "string" && langProp
      ? normalizeLang(langProp)
      : getLangFromPathname(pathname);
  const copy = deepMerge(
    uiCopy[lang] || uiCopy.fi,
    siteContent?.portfolio?.uiCopy?.[lang] || {}
  );
  const resolvedCases = useMemo(() => {
    const remoteCases = siteContent?.portfolio?.cases;
    if (!Array.isArray(remoteCases) || remoteCases.length === 0) return cases;
    return remoteCases.map((remoteCase) => {
      const fallback = cases.find((item) => item.id === remoteCase.id) || {};
      const merged = deepMerge(fallback, remoteCase);
      return {
        ...merged,
        img: remoteCase.imageUrl || fallback.img || merged.img || "",
      };
    });
  }, [siteContent]);
  const resolvedLogos = useMemo(() => {
    const remoteLogos = siteContent?.portfolio?.logos;
    if (!Array.isArray(remoteLogos)) return logos;
    return remoteLogos.map((remoteLogo) => {
      const fallback = logos.find((item) => item.name === remoteLogo.name) || {};
      return {
        ...deepMerge(fallback, remoteLogo),
        src: remoteLogo.imageUrl || fallback.src || "",
      };
    });
  }, [siteContent]);
  const resolvedVideos = useMemo(() => {
    const remoteVideos = siteContent?.portfolio?.videos;
    return Array.isArray(remoteVideos) && remoteVideos.length > 0
      ? remoteVideos
      : videos;
  }, [siteContent]);
  const t = (key, vars, fallbackText) => {
    const result = translate(key, lang, vars);
    if (result === key && typeof fallbackText === "string") {
      return fallbackText;
    }
    return result;
  };
  const featuredCases = useMemo(
    () => resolvedCases.filter((project) => project.featured),
    [resolvedCases]
  );
  const websiteCases = useMemo(
    () => resolvedCases.filter((project) => !project.featured),
    [resolvedCases]
  );
  const pricingHref =
    lang === "fi"
      ? "/verkkosivujen-hinta/"
      : `/${lang}/verkkosivujen-hinta/`;
  const businessHref =
    lang === "fi"
      ? "/verkkosivut-yrityksille/"
      : `/${lang}/verkkosivut-yrityksille/`;
  useEffect(() => {
    const timer = window.setTimeout(() => setIsReady(true), 80);
    return () => {
      window.clearTimeout(timer);
    };
  }, []);
  useEffect(() => {
    if (!activeVideo || typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setActiveVideo(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [activeVideo]);
  const getCaseCopy = (project) => {
    // Case-tekstit pidetään tässä komponentissa kolmikielisinä, jotta
    // portfolio ei vahingossa käytä vanhoja i18n-kuvauksia.
    const localized = project.localized?.[lang] || project.localized?.fi;
    return {
      eyebrow: localized?.eyebrow || "",
      title: localized?.title || project.alt,
      desc: localized?.desc || "",
      bullets: localized?.bullets || [],
    };
  };
  return (
    <section
      id="works"
      className="mx-auto w-full scroll-mt-24 px-2 pb-24 sm:px-4 md:px-6 lg:px-12 xl:px-16"
      aria-label={t("works.ariaLabel", null, "Portfolio")}
    >
      <div className="mx-auto w-full max-w-6xl">
        <div className="relative overflow-hidden rounded-[2rem] border border-black/10 bg-white/70 px-4 py-8 shadow-[0_20px_80px_rgba(0,0,0,0.08)] backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.04] dark:shadow-[0_20px_80px_rgba(0,0,0,0.25)] sm:px-6 md:px-10 md:py-10">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -left-28 top-24 h-72 w-72 rounded-full bg-lime-400/10 blur-3xl"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-28 top-[38rem] h-80 w-80 rounded-full bg-sky-400/10 blur-3xl"
          />
          <header
            className={`relative z-10 mx-auto max-w-4xl text-center transition-all duration-700 ease-out ${
              isReady ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"
            }`}
          >
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-black/50 dark:text-white/50">
              {copy.badge}
            </p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-black dark:text-white md:text-4xl lg:text-5xl">
              {copy.title}
            </h2>
            <p className="mx-auto mt-4 max-w-3xl text-base font-light leading-relaxed text-black/75 dark:text-white/75 md:text-lg">
              {copy.lead}
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
              {copy.proof.map((label) => (
                <span
                  key={label}
                  className="rounded-full border border-black/10 bg-white/75 px-3 py-1.5 text-xs font-bold text-black/70 dark:border-white/10 dark:bg-white/5 dark:text-white/75"
                >
                  {label}
                </span>
              ))}
            </div>
          </header>
          {/* Laajemmat verkkopalvelut */}
          <section className="relative z-10 mt-12">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-lime-700 dark:text-lime-400">
                {copy.featuredEyebrow}
              </p>
              <h3 className="mt-2 text-2xl font-bold text-black dark:text-white md:text-3xl">
                {copy.featuredTitle}
              </h3>
              <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-black/70 dark:text-white/70 md:text-base">
                {copy.featuredLead}
              </p>
            </div>
            <div className="mt-7 grid gap-5 lg:grid-cols-2">
              {featuredCases.map((project) => {
                const projectCopy = getCaseCopy(project);
                return (
                  <article
                    key={project.id}
                    className="group relative flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-black/10 bg-white/80 p-4 transition hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-white/5 sm:p-5"
                  >
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_10%,rgba(132,204,22,0.10),transparent_38%)] opacity-80" />
                    <div className="relative z-10">
                      <ProjectImage project={project} featured openLabel={copy.openSite} />
                      <div className="mt-5">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <span className="rounded-full border border-black/10 bg-white/75 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.16em] text-black/60 dark:border-white/10 dark:bg-white/5 dark:text-white/60">
                            {projectCopy.eyebrow}
                          </span>
                          <FiCode className="text-xl text-lime-600 dark:text-lime-400" />
                        </div>
                        <h4 className="mt-4 text-2xl font-black leading-tight text-black dark:text-white sm:text-3xl">
                          {projectCopy.title}
                        </h4>
                        <p className="mt-3 text-sm leading-relaxed text-black/70 dark:text-white/75 sm:text-base">
                          {projectCopy.desc}
                        </p>
                        <div className="mt-4 flex flex-wrap gap-2" aria-label={copy.techLabel}>
                          {project.tech.map((technology) => (
                            <span
                              key={technology}
                              className="rounded-full border border-black/10 bg-black/[0.03] px-2.5 py-1 text-[11px] font-bold text-black/60 dark:border-white/10 dark:bg-white/5 dark:text-white/70"
                            >
                              {technology}
                            </span>
                          ))}
                        </div>
                        <ul className="mt-5 space-y-2.5">
                          {projectCopy.bullets.map((bullet) => (
                            <li
                              key={bullet}
                              className="flex items-start gap-2.5 text-sm leading-relaxed text-black/70 dark:text-white/70"
                            >
                              <FiCheck className="mt-0.5 shrink-0 text-lg text-lime-600 dark:text-lime-400" />
                              <span>{bullet}</span>
                            </li>
                          ))}
                        </ul>
                        <a
                          href={project.href}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-6 inline-flex items-center gap-2 rounded-full bg-black px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:shadow-lg dark:bg-white dark:text-black"
                        >
                          {copy.openSite}
                          <FiArrowUpRight aria-hidden="true" />
                        </a>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
          {/* Kaikki verkkosivut näkyvissä ilman karusellia */}
          <section className="relative z-10 mt-14">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-black/50 dark:text-white/50">
                {copy.sitesEyebrow}
              </p>
              <h3 className="mt-2 text-2xl font-bold text-black dark:text-white md:text-3xl">
                {copy.sitesTitle}
              </h3>
              <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-black/70 dark:text-white/70 md:text-base">
                {copy.sitesLead}
              </p>
            </div>
            <div className="mt-7 grid gap-5 md:grid-cols-2">
              {websiteCases.map((project) => {
                const projectCopy = getCaseCopy(project);
                return (
                  <article
                    key={project.id}
                    className="group flex h-full flex-col rounded-[1.6rem] border border-black/10 bg-white/80 p-4 transition hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-white/5 sm:p-5"
                  >
                    <ProjectImage project={project} openLabel={copy.openSite} />
                    <div className="mt-5 flex flex-1 flex-col">
                      <span className="w-fit rounded-full border border-black/10 bg-white/70 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.16em] text-black/60 dark:border-white/10 dark:bg-white/5 dark:text-white/60">
                        {projectCopy.eyebrow}
                      </span>
                      <h4 className="mt-3 text-xl font-black leading-snug text-black dark:text-white sm:text-2xl">
                        {projectCopy.title}
                      </h4>
                      <p className="mt-3 text-sm leading-relaxed text-black/70 dark:text-white/75 sm:text-base">
                        {projectCopy.desc}
                      </p>
                      <div className="mt-4 flex flex-wrap gap-2" aria-label={copy.techLabel}>
                        {project.tech.map((technology) => (
                          <span
                            key={technology}
                            className="rounded-full border border-black/10 bg-black/[0.03] px-2.5 py-1 text-[11px] font-bold text-black/60 dark:border-white/10 dark:bg-white/5 dark:text-white/70"
                          >
                            {technology}
                          </span>
                        ))}
                      </div>
                      <ul className="mt-4 space-y-2">
                        {projectCopy.bullets.slice(0, 2).map((bullet) => (
                          <li
                            key={bullet}
                            className="flex items-start gap-2 text-sm leading-relaxed text-black/70 dark:text-white/75"
                          >
                            <span className="mt-[7px] h-2 w-2 shrink-0 rounded-full bg-lime-400" />
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                      <a
                        href={project.href}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-auto inline-flex items-center gap-2 pt-5 text-sm font-bold text-black transition group-hover:translate-x-1 dark:text-white"
                      >
                        {copy.openSite}
                        <FiArrowUpRight aria-hidden="true" />
                      </a>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
          {/* Logo- ja brändityöt suoraan näkyvissä */}
          <section className="relative z-10 mt-14 border-t border-black/10 pt-12 dark:border-white/10">
            <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-black/50 dark:text-white/50">
                {copy.logosEyebrow}
              </p>
              <h3 className="mt-2 text-2xl font-bold text-black dark:text-white md:text-3xl">
                {copy.logosTitle}
              </h3>
              <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-black/70 dark:text-white/70 md:text-base">
                {copy.logosLead}
              </p>
            </div>
            <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
              {resolvedLogos.map((logo) => {
                const card = (
                  <div className="group flex aspect-square items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-white/80 p-3 shadow-sm transition hover:-translate-y-1 hover:shadow-lg dark:border-white/10 dark:bg-white/[0.04] sm:p-4">
                    <img
                      src={logo.src}
                      alt={logo.name}
                      loading="lazy"
                      className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-[1.04]"
                    />
                  </div>
                );
                return logo.link ? (
                  <a
                    key={logo.name}
                    href={logo.link}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${copy.openSite}: ${logo.name}`}
                    title={logo.name}
                  >
                    {card}
                  </a>
                ) : (
                  <div key={logo.name} title={logo.name}>
                    {card}
                  </div>
                );
              })}
            </div>
          </section>
          {/* Videot näkyvissä thumbnail-kortteina */}
          <section className="relative z-10 mt-14 border-t border-black/10 pt-12 dark:border-white/10">
            <div className="mx-auto max-w-3xl text-center">
               <p className="text-xs font-bold uppercase tracking-[0.22em] text-black/50 dark:text-white/50">
                {copy.videosEyebrow}
              </p>
              <h3 className="mt-2 text-2xl font-bold text-black dark:text-white md:text-3xl">
                {copy.videosTitle}
              </h3>
              <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-black/70 dark:text-white/70 md:text-base">
                {copy.videosLead}
              </p>
            </div>
            <div className="mt-7 grid gap-4 md:grid-cols-2">
              {resolvedVideos.map((video, index) => {
                const videoTitle = video.title[lang] || video.title.fi;
                return (
                <button
                  key={`${video.id}-${index}`}
                  type="button"
                  onClick={() => setActiveVideo({ ...video, resolvedTitle: videoTitle })}
                  className="group overflow-hidden rounded-2xl border border-black/10 bg-white/80 text-left transition hover:-translate-y-1 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-lime-400/70 dark:border-white/10 dark:bg-white/5"
                  aria-label={`${copy.playVideo}: ${videoTitle}`}
                >
                  <div className="relative aspect-video overflow-hidden bg-black">
                    <img
                      src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`}
                      alt=""
                      loading="lazy"
                      className="h-full w-full object-cover opacity-90 transition duration-500 group-hover:scale-[1.03] group-hover:opacity-100"
                      aria-hidden="true"
                    />
                    <div className="absolute inset-0 bg-black/20 transition group-hover:bg-black/5" />
                    <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-xl text-black shadow-2xl transition group-hover:scale-110">
                      <FiPlay className="ml-0.5" aria-hidden="true" />
                    </span>
                  </div>
                  <div className="p-4 sm:p-5">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-black/50 dark:text-white/50">
                      {copy.playVideo}
                    </p>
                    <h4 className="mt-2 text-base font-bold leading-snug text-black dark:text-white sm:text-lg">
                      {videoTitle}
                    </h4>
                  </div>
                </button>
                );
              })}
            </div>
          </section>
          {/* Hinnasto ja yhteydenotto */}
          <section className="relative z-10 mt-14 border-t border-black/10 pt-12 dark:border-white/10">
            <div className="relative overflow-hidden rounded-[1.75rem] border border-black/10 bg-white/80 px-5 py-7 text-center dark:border-white/10 dark:bg-white/5 sm:px-8 sm:py-9">
              <div className="pointer-events-none absolute -left-20 -top-20 h-56 w-56 rounded-full bg-lime-400/10 blur-3xl" />
              <div className="relative z-10 mx-auto max-w-3xl">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-lime-700 dark:text-lime-400">
                  {copy.pricingEyebrow}
                </p>
                <h3 className="mt-2 text-2xl font-bold text-black dark:text-white md:text-3xl">
                  {copy.pricingTitle}
                </h3>
                <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-black/70 dark:text-white/70 md:text-base">
                  {copy.pricingLead}
                </p>
                <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <a
                    href={pricingHref}
                    className="inline-flex items-center justify-center rounded-full border border-black/15 bg-white/80 px-5 py-3 text-sm font-bold text-black transition hover:-translate-y-0.5 hover:shadow-lg dark:border-white/15 dark:bg-white/5 dark:text-white"
                  >
                    {copy.pricingCta}
                    <FiArrowUpRight className="ml-2" aria-hidden="true" />
                  </a>
                  <a
                    href={businessHref}
                    className="inline-flex items-center justify-center rounded-full border border-lime-400/40 bg-lime-400/10 px-5 py-3 text-sm font-bold text-black transition hover:-translate-y-0.5 hover:shadow-lg dark:text-white"
                  >
                    {copy.businessCta}
                    <FiArrowUpRight className="ml-2" aria-hidden="true" />
                  </a>
                  <ScrollLink
                    to="contact"
                    smooth
                    duration={600}
                    offset={-96}
                    className="cursor-pointer"
                  >
                    <ThemedButton>{copy.contactCta}</ThemedButton>
                  </ScrollLink>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
      {/* YouTube ladataan vasta tässä vaiheessa, kun käyttäjä avaa videon. */}
      {activeVideo ? (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 p-3 backdrop-blur-sm sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-label={activeVideo.resolvedTitle}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setActiveVideo(null);
            }
          }}
        >
          <div className="w-full max-w-5xl overflow-hidden rounded-[1.5rem] border border-white/10 bg-black shadow-2xl">
            <div className="flex items-center justify-between gap-4 border-b border-white/10 px-4 py-3 sm:px-5">
              <p className="min-w-0 truncate text-sm font-bold text-white sm:text-base">
                {activeVideo.resolvedTitle}
              </p>
              <button
                type="button"
                onClick={() => setActiveVideo(null)}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-lime-400/70"
                aria-label={copy.closeVideo}
                title={copy.closeVideo}
              >
                <FiX aria-hidden="true" />
              </button>
            </div>
            <div className="aspect-video bg-black">
              <iframe
                src={`https://www.youtube.com/embed/${activeVideo.id}?autoplay=1&rel=0`}
                title={activeVideo.resolvedTitle}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="h-full w-full"
              />
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};
export default Works;